import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { format } from 'date-fns';
import { Button } from 'primereact/button';

import {
  StudentScholarshipsEntity,
  StudentScholarshipsStatus,
} from '../../../../../api/types/students/student-scholarships/student-scholarship-entity';
import globalUserPage from '../../../../../stores/global-user-page';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';
import { AddStudentScholarshipDialog } from './components/add-student-scholarship-dialog';
import { UpdateStudentScholarshipStatusDialog } from './components/update-student-scholarship-status-dialog';
import { deleteStudentScholarshipOperation } from './operations/delete-student-scholarship-operation';
import { WarningDialog } from '../../../../../component/ui/warning-dialog/warning-dialog';

interface UserPageStudentScholarshipsProps {
  user: UserSummaryType;
}

export const UserPageStudentScholarships: React.FC<UserPageStudentScholarshipsProps> =
  observer(({ user }) => {
    const scholarships = useMemo<StudentScholarshipsEntity[]>(() => {
      return globalUserPage.studentDetails?.studentScholarships ?? [];
    }, [globalUserPage.studentDetails?.studentScholarships]);

    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [selectedScholarship, setSelectedScholarship] =
      useState<StudentScholarshipsEntity | null>(null);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [scholarshipToDelete, setScholarshipToDelete] =
      useState<StudentScholarshipsEntity | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const formatDate = (date: Date | string | undefined) => {
      if (!date) return '';
      return format(new Date(date), 'dd/MM/yyyy');
    };

    const formatAmount = (amount: number | undefined) => {
      if (amount === undefined || amount === null) return '';
      return `₪${amount.toLocaleString()}`;
    };

    const getStatusText = (status: StudentScholarshipsStatus | undefined) => {
      if (!status) return '';
      switch (status) {
        case StudentScholarshipsStatus.PENDING:
          return 'ממתינה';
        case StudentScholarshipsStatus.APPROVED:
          return 'מאושרת';
        case StudentScholarshipsStatus.FAILED:
          return 'נכשלה';
        case StudentScholarshipsStatus.CANCELLED:
          return 'בוטלה';
        default:
          return status;
      }
    };

    const statusTemplate = (rowData: StudentScholarshipsEntity) => {
      const status = rowData.studentScholarshipsStatus;
      let color = '#374151';

      if (status === StudentScholarshipsStatus.APPROVED) {
        color = '#16a34a';
      } else if (status === StudentScholarshipsStatus.FAILED) {
        color = '#ef4444';
      } else if (status === StudentScholarshipsStatus.PENDING) {
        color = '#f59e0b';
      } else if (status === StudentScholarshipsStatus.CANCELLED) {
        color = '#9ca3af';
      }

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: 'flex-start',
          }}
        >
          <span style={{ color, fontWeight: 500 }}>
            {getStatusText(status)}
          </span>
          <Button
            icon="pi pi-refresh"
            rounded
            tooltip={'שינוי סטטוס מלגה'}
            text
            severity="info"
            onClick={() => handleOpenStatusDialog(rowData)}
          />
        </div>
      );
    };

    const remainingAmountTemplate = (rowData: StudentScholarshipsEntity) => {
      const remaining = rowData.scholarshipSum - rowData.consumeSum;
      return (
        <span style={{ direction: 'ltr', display: 'inline-block' }}>
          {formatAmount(remaining)}
        </span>
      );
    };

    const handleOpenStatusDialog = (scholarship: StudentScholarshipsEntity) => {
      setSelectedScholarship(scholarship);
      setShowStatusDialog(true);
    };

    const handleDeleteScholarship = (
      scholarship: StudentScholarshipsEntity,
    ) => {
      setScholarshipToDelete(scholarship);
      setShowDeleteWarning(true);
    };

    const handleDeleteConfirm = async () => {
      if (!scholarshipToDelete) return;

      setDeleteLoading(true);
      try {
        await deleteStudentScholarshipOperation(
          user.id,
          scholarshipToDelete.id,
        );
        setShowDeleteWarning(false);
        setScholarshipToDelete(null);
      } catch (error) {
        // Error handling is done in the operation
      } finally {
        setDeleteLoading(false);
      }
    };

    if (scholarships.length === 0) {
      return (
        <div style={{ width: '100%', padding: '24px' }}>
          <AddStudentScholarshipDialog
            visible={showAddDialog}
            userId={user.id}
            onHide={() => setShowAddDialog(false)}
          />
          <div
            style={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              padding: '32px',
              textAlign: 'center',
              backgroundColor: '#ffffff',
            }}
          >
            <i
              className="pi pi-gift"
              style={{
                fontSize: '40px',
                color: '#9ca3af',
                marginBottom: '12px',
              }}
            />
            <h3 style={{ margin: 0, color: '#4b5563' }}>
              אין מלגות לסטודנט זה
            </h3>
            <p style={{ margin: '8px 0 16px', color: '#6b7280' }}>
              לא נמצאו נתוני מלגות עבור המשתמש הנוכחי.
            </p>
            <Button
              label="הוסף מלגה ראשונה"
              icon="pi pi-plus"
              onClick={() => setShowAddDialog(true)}
              style={{
                backgroundColor: '#16a34a',
                border: 'none',
                color: 'white',
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div style={{ width: '100%', padding: '20px' }}>
        <AddStudentScholarshipDialog
          visible={showAddDialog}
          userId={user.id}
          onHide={() => setShowAddDialog(false)}
        />

        <UpdateStudentScholarshipStatusDialog
          visible={showStatusDialog}
          userId={user.id}
          scholarship={selectedScholarship}
          onHide={() => {
            setShowStatusDialog(false);
            setSelectedScholarship(null);
          }}
        />

        <WarningDialog
          header="מחיקת מלגה"
          isVisible={showDeleteWarning}
          onHide={() => {
            if (!deleteLoading) {
              setShowDeleteWarning(false);
              setScholarshipToDelete(null);
            }
          }}
          addIcon
          content="האם אתה בטוח שברצונך למחוק את המלגה?"
          dangerousContent="פעולה זו אינה ניתנת לביטול."
          onConfirm={handleDeleteConfirm}
          confirmLabel="מחק"
          confirmLoading={deleteLoading}
          cancelLabel="ביטול"
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <h2 style={{ margin: 0 }}>מלגות סטודנט</h2>
          <Button
            icon="pi pi-plus"
            onClick={() => setShowAddDialog(true)}
            style={{
              backgroundColor: '#16a34a',
              border: 'none',
              color: 'white',
            }}
          />
        </div>

        <DataTable
          value={scholarships}
          emptyMessage="לא נמצאו מלגות"
          style={{ direction: 'rtl' }}
        >
          {/*          <Column
            field="id"
            header="מזהה מלגה"
            sortable
            style={{ textAlign: 'right' }}
          />*/}
          <Column
            field="scholarshipSum"
            header="סכום מלגה"
            body={(rowData: StudentScholarshipsEntity) => (
              <span style={{ direction: 'ltr', display: 'inline-block' }}>
                {formatAmount(rowData.scholarshipSum)}
              </span>
            )}
            sortable
            style={{ textAlign: 'right' }}
          />

          <Column
            field="consumeSum"
            header="סכום שנוצל"
            body={(rowData: StudentScholarshipsEntity) => (
              <span style={{ direction: 'ltr', display: 'inline-block' }}>
                {formatAmount(rowData.consumeSum)}
              </span>
            )}
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            header="יתרה"
            body={remainingAmountTemplate}
            style={{ textAlign: 'right' }}
          />
          <Column
            field="studentScholarshipsStatus"
            header="סטטוס"
            body={statusTemplate}
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            body={(rowData: StudentScholarshipsEntity) =>
              formatDate(rowData.createdAt)
            }
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            field="createdByName"
            header="נוצר ע״י"
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            header="פעולות"
            body={(rowData: StudentScholarshipsEntity) => (
              <Button
                icon="pi pi-trash"
                rounded
                text
                severity="danger"
                onClick={() => handleDeleteScholarship(rowData)}
              />
            )}
            style={{ textAlign: 'right', width: '140px' }}
          />
        </DataTable>
      </div>
    );
  });
